import { Link } from 'react-router'

const blogCategoryPageRoute = (category: string, page: number) =>
  page === 1
    ? `/blog/categories/${encodeURIComponent(category)}`
    : `/blog/categories/${encodeURIComponent(category)}/older/${page}`
const blogPageRoute = (page: number) =>
  page === 1 ? '/blog' : `/blog/older/${page}`
const articlePageRoute = (page: number) =>
  page === 1 ? '/articles' : `/articles/older/${page}`

export const BlogPagination = ({
  categorySlug,
  page,
  totalPages,
}: {
  categorySlug?: string
  page: number
  totalPages: number
}) => {
  return (
    <Pagination
      ariaLabel="Blog pagination"
      page={page}
      route={(page) =>
        categorySlug
          ? blogCategoryPageRoute(categorySlug, page)
          : blogPageRoute(page)
      }
      totalPages={totalPages}
    />
  )
}

export const ArticlePagination = ({
  page,
  totalPages,
}: {
  page: number
  totalPages: number
}) => {
  return (
    <Pagination
      ariaLabel="Articles pagination"
      page={page}
      route={articlePageRoute}
      totalPages={totalPages}
    />
  )
}

const Pagination = ({
  ariaLabel,
  page,
  route,
  totalPages,
}: {
  ariaLabel: string
  page: number
  route: (page: number) => string
  totalPages: number
}) => {
  if (totalPages <= 1) return null

  const windowSize = 1
  const lastPage = totalPages
  const start = Math.max(1, page - windowSize)
  const end = Math.min(lastPage, page + windowSize)
  const pages: Array<number | 'ellipsis'> = []

  pages.push(1)

  if (start > 2) {
    pages.push('ellipsis')
  }

  for (let i = Math.max(2, start); i <= Math.min(end, lastPage - 1); i += 1) {
    pages.push(i)
  }

  if (end < lastPage - 1) {
    pages.push('ellipsis')
  }

  if (lastPage > 1) {
    pages.push(lastPage)
  }

  const hasPrev = page > 1
  const hasNext = page < totalPages

  return (
    <nav className="blog-pagination" aria-label={ariaLabel}>
      {hasPrev && (
        <Link
          className="blog-pagination-arrow carousel-button-next carousel-button-next--previous"
          to={route(page - 1)}
          rel="prev"
          aria-label="Previous page"
        >
          <PaginationArrowIcon />
        </Link>
      )}

      <div className="blog-pagination-pages">
        {pages.map((pageNumber, index) => {
          if (pageNumber === 'ellipsis') {
            return (
              <span
                key={`ellipsis-${index}`}
                className="blog-pagination-ellipsis"
              >
                &hellip;
              </span>
            )
          }

          const isCurrent = pageNumber === page
          if (isCurrent) {
            return (
              <span
                key={pageNumber}
                className="blog-pagination-current"
                aria-current="page"
              >
                {pageNumber}
              </span>
            )
          }

          return (
            <Link
              className={
                pageNumber === 1 || pageNumber === lastPage
                  ? 'blog-pagination-boundary'
                  : undefined
              }
              key={pageNumber}
              to={route(pageNumber)}
            >
              {pageNumber}
            </Link>
          )
        })}
      </div>

      {hasNext && (
        <Link
          className="blog-pagination-arrow carousel-button-next carousel-button-next--next"
          to={route(page + 1)}
          rel="next"
          aria-label="Next page"
        >
          <PaginationArrowIcon />
        </Link>
      )}
    </nav>
  )
}

const PaginationArrowIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    viewBox="0 0 19 13"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.3052 5.58525C17.7899 5.58525 18.1828 5.97818 18.1828 6.46289C18.1828 6.9476 17.7899 7.34053 17.3052 7.34053L17.3052 6.46289L17.3052 5.58525ZM0.257082 7.08348C-0.085659 6.74074 -0.0856591 6.18505 0.257082 5.84231L5.84236 0.257024C6.1851 -0.0857167 6.7408 -0.0857168 7.08354 0.257024C7.42628 0.599764 7.42628 1.15546 7.08354 1.4982L2.11884 6.46289L7.08354 11.4276C7.42628 11.7703 7.42628 12.326 7.08354 12.6688C6.7408 13.0115 6.1851 13.0115 5.84236 12.6688L0.257082 7.08348ZM17.3052 6.46289L17.3052 7.34053L0.877668 7.34053L0.877668 6.46289L0.877668 5.58525L17.3052 5.58525L17.3052 6.46289Z"
      fill="currentColor"
    />
  </svg>
)
